import axios from 'axios';

/**
 * SDK Partner admin client.
 *
 * Các endpoint admin nằm ở GATEWAY (khác VUE_APP_API của DiaB backend).
 * Base URL đọc từ VUE_APP_SDK_GATEWAY. Token admin lấy qua
 * POST /api/admin/auth/login (creds dev đọc từ env, hoặc login thủ công qua form),
 * cache ở sessionStorage và gắn Authorization: Bearer cho mọi call.
 * Gặp 401 -> xóa token + login lại (nếu có creds env) rồi retry 1 lần.
 */

const GATEWAY = process.env.VUE_APP_SDK_GATEWAY;
const TOKEN_KEY = 'sdk_admin_token';
const CAN_VIEW_LOGS_KEY = 'sdk_admin_can_view_logs';

const gateway = axios.create({
  baseURL: GATEWAY,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

function getToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

function setToken(token) {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
}

function clearToken() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(CAN_VIEW_LOGS_KEY);
}

function setCanViewLogs(value) {
  sessionStorage.setItem(CAN_VIEW_LOGS_KEY, value ? '1' : '0');
}

// null = this session never learned it (token issued before the flag existed).
function getCanViewLogs() {
  const v = sessionStorage.getItem(CAN_VIEW_LOGS_KEY);
  return v === null ? null : v === '1';
}

async function login(email, password) {
  const res = await gateway.post('/api/admin/auth/login', { email, password });
  const payload = res.data || {};
  const data = payload.data || payload;
  const token = data.token || payload.token || null;
  if (!token) {
    throw new Error('Không lấy được token đăng nhập từ gateway');
  }
  setToken(token);
  // canViewRequestLogs: backend trả về trong login response (contract §4)
  const canViewLogs =
    typeof data.canViewRequestLogs !== 'undefined'
      ? data.canViewRequestLogs
      : typeof payload.canViewRequestLogs !== 'undefined'
        ? payload.canViewRequestLogs
        : false;
  setCanViewLogs(canViewLogs);
  return token;
}

function hasEnvCredentials() {
  return !!(
    process.env.VUE_APP_SDK_ADMIN_EMAIL &&
    process.env.VUE_APP_SDK_ADMIN_PASSWORD
  );
}

/** Đăng nhập bằng env creds — mọi thất bại (creds sai/gateway lỗi) đều quy về
 * SDK_ADMIN_NO_TOKEN thay vì để lỗi axios thô lọt ra ngoài, để loadData() luôn
 * nhận diện được và hiện form đăng nhập thay vì trang rỗng im lặng. */
async function tryEnvLogin() {
  try {
    return await login(
      process.env.VUE_APP_SDK_ADMIN_EMAIL,
      process.env.VUE_APP_SDK_ADMIN_PASSWORD,
    );
  } catch (e) {
    clearToken();
    throw new Error('SDK_ADMIN_NO_TOKEN');
  }
}

async function ensureToken() {
  const token = getToken();
  if (token) return token;
  if (hasEnvCredentials()) return tryEnvLogin();
  throw new Error('SDK_ADMIN_NO_TOKEN');
}

async function request(config, allowRetry = true) {
  try {
    await ensureToken();
  } catch (e) {
    throw new Error('SDK_ADMIN_NO_TOKEN');
  }
  try {
    const res = await gateway.request({
      ...config,
      headers: {
        ...(config.headers || {}),
        Authorization: `Bearer ${getToken()}`,
      },
    });
    return res.data;
  } catch (error) {
    const status = error && error.response && error.response.status;
    if (status === 401) {
      clearToken();
      if (allowRetry && hasEnvCredentials()) {
        try {
          await tryEnvLogin();
          return request(config, false);
        } catch (e) {
          throw new Error('SDK_ADMIN_NO_TOKEN');
        }
      }
      // Không có env creds (hoặc vừa retry vẫn 401) — token đã bị xóa ở trên,
      // để loadData() hiện form đăng nhập thay vì toast lỗi rồi để trang rỗng.
      throw new Error('SDK_ADMIN_NO_TOKEN');
    }
    throw normalizeError(error);
  }
}

function normalizeError(error) {
  if (error && error.response && error.response.data) {
    const data = error.response.data;
    const message =
      (data.error && data.error.message) ||
      data.message ||
      `Gateway lỗi (${error.response.status})`;
    return { code: error.response.status, message };
  }
  if (error && error.message) return { code: 'unknown', message: error.message };
  return { code: 'unknown', message: 'Gateway không phản hồi' };
}

export default {
  /** Đã cấu hình VUE_APP_SDK_GATEWAY chưa */
  isConfigured() {
    return !!GATEWAY;
  },
  /** Base URL của gateway — cũng là BASE_URL mà SDK đối tác trỏ tới (để chia sẻ cho partner) */
  baseUrl() {
    return GATEWAY || '';
  },
  /** Có creds dev trong env để auto-login không */
  hasEnvCredentials,
  /** Đang có token trong session chưa */
  hasToken() {
    return !!getToken();
  },
  /** Login thủ công (dùng cho form login nhỏ) */
  async login(email, password) {
    try {
      return await login(email, password);
    } catch (error) {
      throw normalizeError(error);
    }
  },
  logout() {
    clearToken();
  },

  // ==== SDK Partners ====
  // GET /api/admin/sdk-partners?search=&status=  -> PartnerKey[]
  listPartners(params = {}) {
    return request({ method: 'get', url: '/api/admin/sdk-partners', params });
  },
  // POST /api/admin/sdk-partners -> { partner, apiKey }
  createPartner(body) {
    return request({ method: 'post', url: '/api/admin/sdk-partners', data: body });
  },
  // PUT /api/admin/sdk-partners/{id} -> PartnerKey
  updatePartner(id, body) {
    return request({ method: 'put', url: `/api/admin/sdk-partners/${id}`, data: body });
  },
  // PATCH /api/admin/sdk-partners/{id} -> reactivate
  reactivatePartner(id) {
    return request({
      method: 'patch',
      url: `/api/admin/sdk-partners/${id}`,
      data: { isActive: true },
    });
  },
  // DELETE /api/admin/sdk-partners/{id} -> revoke (soft delete)
  revokePartner(id) {
    return request({ method: 'delete', url: `/api/admin/sdk-partners/${id}` });
  },
  // POST /api/admin/sdk-partners/{id}/rotate -> { apiKey }
  rotateKey(id) {
    return request({ method: 'post', url: `/api/admin/sdk-partners/${id}/rotate` });
  },
  // GET /api/admin/sdk-partners/{id}/logs?page&pageSize&action&from&to -> { data, total, page, pageSize }
  getLogs(id, params = {}) {
    return request({
      method: 'get',
      url: `/api/admin/sdk-partners/${id}/logs`,
      params,
    });
  },
  // GET /api/admin/sdk-partners/{id}/usage -> UsageStats
  getUsage(id) {
    return request({ method: 'get', url: `/api/admin/sdk-partners/${id}/usage` });
  },
  // GET /api/admin/sale-packages -> [{ id, name, ... }] (gói bundle bên App API)
  async getSalePackages() {
    return request({ method: 'get', url: '/api/admin/sale-packages' });
  },
  // GET /api/admin/sdk-partners/{id}/secret -> { accessTokenSecret }
  getSecret(id) {
    return request({ method: 'get', url: `/api/admin/sdk-partners/${id}/secret` });
  },
  // POST /api/admin/sdk-partners/{id}/sign-token -> { token, exp }
  signToken(id, body) {
    return request({ method: 'post', url: `/api/admin/sdk-partners/${id}/sign-token`, data: body });
  },

  // ==== Stats (§3.1) ====
  // GET /api/admin/stats?by=partner|service|customer|day|event&partnerId=&service=&eventType=&from=&to=
  // -> { rows:[{key,count}], totals:{events,customers,booked,cancelled,blocked,logins} }
  getStats(params = {}) {
    return request({ method: 'get', url: '/api/admin/stats', params });
  },

  // ==== Request Logs (§3.3, §3.4) ====
  // GET /api/admin/request-logs?requestId=&phone=&partnerId=&status=&serviceType=&path=&from=&to=&page=&pageSize=
  // -> { total, items:[{id,requestId,partnerId,partnerName,phone,userId,method,path,serviceType,statusCode,durationMs,createdAt}] }
  getRequestLogs(params = {}) {
    return request({ method: 'get', url: '/api/admin/request-logs', params });
  },
  // GET /api/admin/request-logs/{id}  -> full item + requestBody, responseBody
  getRequestLogDetail(id) {
    return request({ method: 'get', url: `/api/admin/request-logs/${id}` });
  },
  // GET /api/admin/request-logs/by-request-id/{requestId}  -> same as detail (§3.5)
  getRequestLogByRequestId(requestId) {
    return request({
      method: 'get',
      url: `/api/admin/request-logs/by-request-id/${encodeURIComponent(requestId)}`,
    });
  },

  // ==== Service Events (§3.2) ====
  // GET /api/admin/service-events?phone=&partnerId=&eventType=&service=&from=&to=&page=&pageSize=
  // -> { total, items:[{id,eventType,partnerId,partnerName,phone,service,resourceId,result,bundleOrderId,reverted,requestId,createdAt}] }
  getServiceEvents(params = {}) {
    return request({ method: 'get', url: '/api/admin/service-events', params });
  },

  /** canViewRequestLogs — đọc từ session, đã được lưu lúc login (null = chưa biết) */
  canViewRequestLogs() {
    return getCanViewLogs() === true;
  },

  /** Phiên đăng nhập cũ chưa có cờ quyền → hỏi Gateway (GET /api/admin/auth/me), không cần đăng nhập lại. */
  async ensureCanViewRequestLogs() {
    const known = getCanViewLogs();
    if (known !== null) return known;
    try {
      const res = await request({ method: 'get', url: '/api/admin/auth/me' });
      const payload = (res && res.data) || res || {};
      const value = !!(typeof payload.canViewRequestLogs !== 'undefined' ? payload.canViewRequestLogs : (payload.data || {}).canViewRequestLogs);
      setCanViewLogs(value);
      return value;
    } catch (e) {
      return false;
    }
  },
};

/** Danh sách feature hỗ trợ (checkbox trong form tạo/sửa) */
export const SDK_FEATURES = [
  { value: 'telemedicine', text: 'Telemedicine' },
  { value: 'webinar', text: 'Webinar' },
  { value: 'chat', text: 'Chat' },
  { value: 'benefit', text: 'Benefit' },
];

/** Các loại action của audit log (dùng cho dropdown filter) */
export const LOG_ACTIONS = [
  'join',
  'leave',
  'create_meeting',
  'end_meeting',
  'webinar_join',
  'webinar_leave',
];

/** ServiceType enum — khớp với gateway §1 */
export const SERVICE_TYPES = [
  'auth',
  'telemedicine',
  'at_clinic',
  'booking',
  'webinar',
  'benefit',
  'voucher',
  'medication',
  'lab_test',
  'payment',
  'partner_api',
  'other',
];

/** EventType enum — khớp với gateway §2 */
export const EVENT_TYPES = [
  'booked',
  'cancelled',
  'login',
  'bundle_activated',
  'quota_blocked',
  'webinar_registered',
  'webinar_joined',
  'medication_requested',
  'prescription_scanned',
  'clinic_result_scanned',
];
